// ─── Feuille : ÉDITER un forfait (nom, prix, limites) ─────────────
// `config/plans` — distinct de `config/fonctionnalites` (palier/jetons, gérés
// dans EcranForfaits). Un forfait n'est ni créable ni supprimable ici : les
// quatre ids sont fixes (cf. PLANS du Core) — c'est une édition pure, donc
// l'action de la Feuille est TOUJOURS 💾 (convention app-wide : ＋ n'a de sens
// que pour un élément qui n'existe pas encore).
import React, { useEffect, useMemo, useState } from 'react';
import { Text, TextInput, View, StyleSheet } from 'react-native';
import { Feuille, Bascule, stylesForm, useUI, useT, estIllimite, TYPES_ELEMENTS, maxTypeDuPlan } from '@mop/core';
import { definirPlan, definirMaxType } from './services/admin';

// Type d'élément → clé i18n de son libellé (ordre d'affichage = TYPES_ELEMENTS).
const LABEL_TYPE = {
  calendriers: 'admin.typeCalendriers',
  inventaires: 'admin.typeInventaires',
  repas: 'admin.typeRepas',
  comptes: 'admin.typeComptes',
};

// Champ numérique qui peut basculer sur "illimité" (valeur null en base).
function ChampLimite({ label, valeur, onChange, s }) {
  const [illimite, setIllimite] = useState(estIllimite(valeur));
  useEffect(() => setIllimite(estIllimite(valeur)), [valeur]);
  return (
    <View style={s.champLimite}>
      <View style={s.champLimiteTete}>
        <Text style={s.lbl}>{label}</Text>
        <View style={s.illimiteRow}>
          <Text style={s.illimiteTxt}>∞</Text>
          <Bascule valeur={illimite} accessibilityLabel={label}
            onChange={(v) => { setIllimite(v); onChange(v ? null : 0); }} />
        </View>
      </View>
      {!illimite && (
        <TextInput style={s.input} value={String(valeur ?? 0)} keyboardType="numeric"
          onChangeText={(v) => onChange(Math.max(0, parseInt(v, 10) || 0))} />
      )}
    </View>
  );
}

export default function FeuillePlan({ visible, planId, plan, cfgFonctions, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [label, setLabel] = useState('');
  const [mensuel, setMensuel] = useState('0');
  const [annuel, setAnnuel] = useState('0');
  const [piecesJointes, setPiecesJointes] = useState(null);
  const [demandesParMois, setDemandesParMois] = useState(null);
  const [historiqueDays, setHistoriqueDays] = useState(null);
  const [pubFixe, setPubFixe] = useState(false);
  const [pubEvenement, setPubEvenement] = useState(false);
  const [exportPdf, setExportPdf] = useState(false);
  const [syncCalendrier, setSyncCalendrier] = useState(false);
  // Plafonds par TYPE d'élément (config/fonctionnalites, PAS config/plans) —
  // un objet { calendriers, inventaires, repas, comptes }, chacun n|null.
  const [maxParType, setMaxParType] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Réinitialise sur la valeur COURANTE à chaque ouverture — la Feuille ne
  // garde pas d'état d'une édition à l'autre.
  useEffect(() => {
    if (!visible || !plan || !planId) return;
    setLabel(plan.label || '');
    setMensuel(String(plan.prix?.mensuel ?? 0));
    setAnnuel(String(plan.prix?.annuel ?? 0));
    setPiecesJointes(plan.piecesJointes ?? null);
    setDemandesParMois(plan.demandesParMois ?? null);
    setHistoriqueDays(plan.historiqueDays ?? null);
    setPubFixe(!!plan.pubFixe);
    setPubEvenement(!!plan.pubEvenement);
    setExportPdf(!!plan.exportPdf);
    setSyncCalendrier(!!plan.syncCalendrier);
    setMaxParType(Object.fromEntries(TYPES_ELEMENTS.map(ty => [ty, maxTypeDuPlan(cfgFonctions, planId, ty)])));
    setErr(''); setBusy(false);
  }, [visible, plan, planId, cfgFonctions]);

  async function enregistrer() {
    if (!planId || busy) return;
    setBusy(true); setErr('');
    try {
      await Promise.all([
        definirPlan(planId, {
          label: label.trim() || planId,
          prix: { mensuel: parseFloat(mensuel.replace(',', '.')) || 0, annuel: parseFloat(annuel.replace(',', '.')) || 0 },
          piecesJointes, demandesParMois, historiqueDays,
          pubFixe, pubEvenement, exportPdf, syncCalendrier,
        }),
        ...TYPES_ELEMENTS.map(ty => definirMaxType(planId, ty, maxParType[ty] ?? null)),
      ]);
      onFermer?.();
    } catch (e) { setErr(e?.message || String(e)); }
    setBusy(false);
  }

  return (
    <Feuille visible={visible} titre={t('admin.modifierForfait')} emoji="🏷️" hauteur={0.86} onFermer={onFermer}
      action={{ icone: '💾', label: t('commun.enregistrer'), disabled: !label.trim() || busy, onPress: enregistrer }}>
      {!!err && <Text style={s.err}>⚠️ {err}</Text>}

      <Text style={s.lbl}>{t('admin.nomAffiche')}</Text>
      <TextInput style={s.input} value={label} onChangeText={setLabel} placeholder={planId} placeholderTextColor={UI.faint} />

      <View style={s.ligne2}>
        <View style={{ flex: 1 }}>
          <Text style={s.lbl}>{t('admin.prixMensuel')}</Text>
          <TextInput style={s.input} value={mensuel} onChangeText={setMensuel} keyboardType="decimal-pad" />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={s.lbl}>{t('admin.prixAnnuel')}</Text>
          <TextInput style={s.input} value={annuel} onChangeText={setAnnuel} keyboardType="decimal-pad" />
        </View>
      </View>

      <ChampLimite label={t('admin.piecesJointesMax')} valeur={piecesJointes} onChange={setPiecesJointes} s={s} />
      <ChampLimite label={t('admin.demandesMoisMax')} valeur={demandesParMois} onChange={setDemandesParMois} s={s} />
      <ChampLimite label={t('admin.historiqueJours')} valeur={historiqueDays} onChange={setHistoriqueDays} s={s} />

      <Text style={[s.lbl, { marginTop: 18 }]}>{t('admin.limitesParType')}</Text>
      {TYPES_ELEMENTS.map(ty => (
        <ChampLimite key={ty} label={t(LABEL_TYPE[ty])} valeur={maxParType[ty] ?? null}
          onChange={(v) => setMaxParType(m => ({ ...m, [ty]: v }))} s={s} />
      ))}
      <Text style={s.aide}>{t('admin.limitesParTypeAide')}</Text>

      <Text style={s.lbl}>{t('admin.options')}</Text>
      {[
        [t('admin.pubFixe'), pubFixe, setPubFixe],
        [t('admin.pubEvenement'), pubEvenement, setPubEvenement],
        [t('admin.exportPdf'), exportPdf, setExportPdf],
        [t('admin.syncCalendrier'), syncCalendrier, setSyncCalendrier],
      ].map(([lbl, val, set]) => (
        <View key={lbl} style={s.optLigne}>
          <Text style={s.optTxt}>{lbl}</Text>
          <Bascule valeur={val} onChange={set} accessibilityLabel={lbl} />
        </View>
      ))}
      <Text style={s.aide}>{t('admin.tarifsAide')}</Text>
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  err: { fontSize: 12.5, color: UI.danger, fontWeight: '700', marginBottom: 6 },
  ligne2: { flexDirection: 'row', gap: 12 },
  champLimite: { marginTop: 12 },
  champLimiteTete: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  illimiteRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  illimiteTxt: { fontSize: 13, color: UI.muted, fontWeight: '700' },
  optLigne: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
              paddingVertical: 9, borderTopWidth: 0.5, borderTopColor: UI.border },
  optTxt: { flex: 1, fontSize: 13.5, color: UI.ink, fontWeight: '600' },
  aide: { fontSize: 11.5, color: UI.faint, marginTop: 14, lineHeight: 16 },
});
